import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useLayoutEffect,
} from 'react'
import { Container } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  SearchWithFilter,
  WorkingDayBadge,
  StatusBadge,
  ActionButtons,
  DataGridComponent,
  ProviderRoleBadge,
} from '@components/common'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import AddProviderModal from '@components/layout/Modals/AddProviderModal/AddProviderModal.component'
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import { EmptyState } from '@components/ui'
import ConfirmDeleteModal from '@components/layout/Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import './css/ProviderManagement.css'
import { DayOfWeek, Provider, WorkingSchedule } from '@dataTypes/provider.types'
import { getFullName } from '@utils/fullname.util'
import { mapDatabaseToProviderData } from '@utils/provider.mapper'
import classNames from 'classnames'
import { ProviderWorkPosition } from '@constants/Providers'
import {
  ColumnModel,
  GridComponent,
  RecordClickEventArgs,
} from '@syncfusion/ej2-react-grids'
import { ClipMode, TextAlign } from '@constants/Common/GridComponent.contants'
import {
  setViewProvider,
  restoreProviderSelection,
} from '@store/reducers/providermanagement.reducer'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { useProviderActions } from '@components/pages/Provider/hooks/useProviderActions'
import SelectProviderContactModal from '@components/layout/Modals/SelectProviderContact/SelectProviderContactModal.component'
import EmailSender from '@components/layout/Modals/EmailSender/EmailSender.component'
import AlertMessage from '@components/layout/Modals/AlertMessage/AlertMessage.component'
import { fetchAllProviders } from '@store/actions'
import { useClinicOperatories, useClinicProviders } from '@hooks'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
// Mock providers for development when backend is unavailable (production-safe)
import { PROVIDER_MANAGEMENT_DEV_MOCKS } from '@assets/mockdata/Provider/mockProviders.data'
import { getEnv } from '@utils/env'
import {
  openSidePanel,
  setActiveSidePanel,
} from '@store/reducers/uiSlice.reducer'

// Helper function for formatting phone numbers in the table (without country code)
const formatPhoneForTable = (phone: string | undefined): string => {
  if (!phone) return ''
  return formatPhoneNumber(phone, false) // false = no country code
}

const GLOBAL_LOADING_CONTAINER = 'body'

const PROVIDER_SELECTION_STORAGE_KEY_PREFIX = 'provider_selection_'

type PersistedProviderSelection = {
  viewProviderId: string | null
  selectedProviderIds: string[]
  isProviderSidePanelOpen: boolean
}

const useProviderDevMocks = (): boolean => {
  if (getEnv('REACT_APP_MOCK_PROVIDER_MANAGEMENT', '') === 'false') {
    return false
  }
  if (getEnv('REACT_APP_MOCK_PROVIDER_MANAGEMENT', '') === 'true') {
    return true
  }
  if (getEnv('REACT_APP_USE_SETTINGS_MOCKS', '') === 'true') {
    return true
  }
  return process.env.NODE_ENV === 'development'
}

const ProviderManagement: React.FC = () => {
  useClinicProviders()
  useClinicOperatories()
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const providersFromStore = useAppSelector((state) => state.provider.data)
  const loading = useAppSelector((state) => state.provider.loading)
  const viewProvider = useAppSelector((state) => state.provider.viewProvider)
  const selectedProviders = useAppSelector(
    (state) => state.provider.selectedProviders,
  )
  const isProviderSidePanelOpen = useAppSelector(
    (state) => state.ui.isSidePanelOpen,
  )
  const hasRehydratedRef = useRef(false)
  const providerGridSectionRef = useRef<HTMLDivElement>(null)
  const providerGridRef = useRef<GridComponent | null>(null)
  const [providerGridBodyMaxHeight, setProviderGridBodyMaxHeight] = useState<
    number | null
  >(null)

  // Initial load: fetch providers every time this page is entered (and when clinic changes)
  useEffect(() => {
    if (selectedClinic) {
      dispatch(fetchAllProviders())
    }
  }, [dispatch, selectedClinic])

  // Persist provider selection to sessionStorage when it changes (keyed by clinic so switching clinic does not restore wrong selection; survives refresh and new tabs)
  const selectedProviderIds = useMemo(
    () => selectedProviders.map((p) => p.provider_id),
    [selectedProviders],
  )
  useEffect(() => {
    const clinicId = selectedClinic?.clinic_id ?? 'none'
    const hasSelection =
      viewProvider?.provider_id != null || selectedProviderIds.length > 0
    if (!hasSelection) {
      return
    }
    const key = `${PROVIDER_SELECTION_STORAGE_KEY_PREFIX}${clinicId}`
    const payload: PersistedProviderSelection = {
      viewProviderId: viewProvider?.provider_id ?? null,
      selectedProviderIds,
      isProviderSidePanelOpen,
    }
    try {
      sessionStorage.setItem(key, JSON.stringify(payload))
    } catch {
      // ignore storage errors
    }
  }, [
    selectedClinic?.clinic_id,
    viewProvider?.provider_id,
    selectedProviderIds,
    isProviderSidePanelOpen,
  ])

  // Rehydrate provider selection from sessionStorage after providers have loaded (e.g. after refresh)
  useEffect(() => {
    if (
      loading ||
      !selectedClinic?.clinic_id ||
      providersFromStore.length === 0 ||
      hasRehydratedRef.current
    ) {
      return
    }
    hasRehydratedRef.current = true
    const key = `${PROVIDER_SELECTION_STORAGE_KEY_PREFIX}${selectedClinic.clinic_id}`
    try {
      const raw = sessionStorage.getItem(key)
      if (!raw) return
      const saved = JSON.parse(raw) as PersistedProviderSelection
      const providerById = new Map(
        providersFromStore.map((p) => [p.provider_id, p]),
      )
      const selectedIds = saved.selectedProviderIds ?? []
      const resolvedSelected = selectedIds
        .map((id) => providerById.get(id))
        .filter((p): p is Provider => p != null)
      const viewProviderResolved =
        saved.viewProviderId != null
          ? (providerById.get(saved.viewProviderId) ?? null)
          : null
      if (resolvedSelected.length > 0 || viewProviderResolved) {
        const viewProviderToRestore = viewProviderResolved
        let selectedProvidersToRestore = resolvedSelected
        if (
          viewProviderToRestore &&
          !selectedProvidersToRestore.some(
            (p) => p.provider_id === viewProviderToRestore.provider_id,
          )
        ) {
          selectedProvidersToRestore = [
            viewProviderToRestore,
            ...selectedProvidersToRestore,
          ]
        }
        dispatch(
          restoreProviderSelection({
            viewProvider: viewProviderToRestore,
            selectedProviders: selectedProvidersToRestore,
          }),
        )
        if (saved.isProviderSidePanelOpen && viewProviderToRestore) {
          dispatch(openSidePanel())
        }
      }
    } catch {
      // ignore parse or storage errors
    }
  }, [loading, selectedClinic?.clinic_id, providersFromStore, dispatch])

  const useDevMocks = useProviderDevMocks()
  const providers =
    providersFromStore.length > 0
      ? providersFromStore
      : useDevMocks
        ? PROVIDER_MANAGEMENT_DEV_MOCKS
        : []
  const showingDevMocks =
    providersFromStore.length === 0 && useDevMocks && providers.length > 0

  const [searchQuery, setSearchQuery] = useState('')
  const [searchFilters, setSearchFilters] = useState<FilterSelection>({})
  const [alertMessageInfo, setAlertMessageInfo] = useState<{
    title: string
    message: string
    type: 'success' | 'error'
    show: boolean
  }>({
    title: '',
    message: '',
    type: 'success',
    show: false,
  })

  const {
    handleCallClick,
    handleEmailClick,
    handleMessageClick,
    confirmDeleteProvider,
    handleAddProvider,
    handleCloseCallModal,
    handleCloseEmailSender,
    handleCloseProviderModal,
    handleCloseConfirmDeleteModal,
    modalState,
  } = useProviderActions()

  // Filter data structure for SearchWithFilter component - dynamically generated from actual provider data
  const filterData: Record<string, string[]> = useMemo(() => {
    const uniqueRoles = Array.from(
      new Set(
        providers
          .map((p) => p.work_role)
          .filter((role): role is string => Boolean(role)),
      ),
    )
    const uniqueEmploymentTypes = Array.from(
      new Set(
        providers
          .map((p) => p.employment_type)
          .filter((type): type is string => Boolean(type)),
      ),
    )
    const uniqueWorkStatuses = Array.from(
      new Set(
        providers
          .map((p) => p.work_status)
          .filter((status): status is string => Boolean(status)),
      ),
    )

    return {
      roles: uniqueRoles,
      employmentStatus: uniqueEmploymentTypes,
      workStatus: uniqueWorkStatuses,
      workingDays: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ],
    }
  }, [providers])

  const filterLabels = {
    roles: 'Staff Role',
    employmentStatus: 'Employment Type',
    workStatus: 'Work Status',
    workingDays: 'Working Days',
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value)
  }

  const handleFilterChange = (selectedFilters: FilterSelection): void => {
    setSearchFilters(selectedFilters)
  }

  const clearAllFilters = (): void => {
    setSearchFilters({})
    setSearchQuery('')
  }

  // Enhanced search and filtering logic - Local filtering only
  const displayData = useMemo((): Provider[] => {
    let dataToFilter = providers

    // Apply search filter locally
    if (searchQuery.trim()) {
      dataToFilter = providers.filter((provider) => {
        const fullName = getFullName(provider).toLowerCase()
        const searchLower = searchQuery.toLowerCase()
        return (
          fullName.includes(searchLower) ||
          provider.provider_id?.toLowerCase().includes(searchLower) ||
          provider.email?.toLowerCase().includes(searchLower) ||
          provider.cellphone?.includes(searchQuery) ||
          provider.work_role?.toLowerCase().includes(searchLower)
        )
      })
    }

    // Apply additional filters
    return dataToFilter.filter((provider) => {
      // Role filter
      if (searchFilters.roles && searchFilters.roles.length > 0) {
        if (!searchFilters.roles.includes(provider.work_role || '')) {
          return false
        }
      }

      // Employment Status filter
      if (
        searchFilters.employmentStatus &&
        searchFilters.employmentStatus.length > 0
      ) {
        if (
          !searchFilters.employmentStatus.includes(
            provider.employment_type || '',
          )
        ) {
          return false
        }
      }

      // Work Status filter
      if (searchFilters.workStatus && searchFilters.workStatus.length > 0) {
        if (!searchFilters.workStatus.includes(provider.work_status || '')) {
          return false
        }
      }

      // Working days filter
      if (searchFilters.workingDays && searchFilters.workingDays.length > 0) {
        const providerWorkingDays =
          provider.workingHours?.map((ws) => ws.day_of_week.toString()) || []
        const hasMatchingDay = searchFilters.workingDays.some((day) =>
          providerWorkingDays.includes(day),
        )
        if (!hasMatchingDay) {
          return false
        }
      }

      return true
    })
  }, [searchQuery, providers, searchFilters])

  // Transform data for grid display
  const transformedData = useMemo(
    () =>
      displayData.map((provider: Provider) => {
        return {
          ...provider,
          providerName: getFullName(provider),
          completeAddress: `${provider.address}, ${provider.city}, ${provider.state}, ${provider.zip}`,
          contactNumber: formatPhoneForTable(provider.cellphone),
        }
      }),
    [displayData],
  )

  const hasNoStaff = providers.length === 0
  const showEmptyState = !loading && transformedData.length === 0

  /** Mirrors the active provider (side panel, tray) onto the grid row. */
  const syncGridSelection = useCallback((): void => {
    const grid = providerGridRef.current
    const selection = grid?.selectionModule
    if (!grid || !selection) return

    let view: Provider[] = []
    try {
      view = (grid.getCurrentViewRecords() as Provider[]) ?? []
    } catch {
      return
    }

    const targetIndex = viewProvider?.provider_id
      ? view.findIndex((row) => row.provider_id === viewProvider.provider_id)
      : -1
    const current = grid.getSelectedRowIndexes() ?? []
    const alreadySynced =
      targetIndex === -1
        ? current.length === 0
        : current.length === 1 && current[0] === targetIndex
    if (alreadySynced) return

    try {
      selection.clearSelection()
      if (targetIndex >= 0) selection.selectRow(targetIndex)
    } catch {
      /* grid not ready */
    }
  }, [viewProvider?.provider_id])

  useEffect(() => {
    syncGridSelection()
  }, [syncGridSelection, transformedData])

  useLayoutEffect(() => {
    if (loading || showEmptyState) return

    const section = providerGridSectionRef.current
    if (!section) return

    const measureGridBodyMaxHeight = (): void => {
      const pageContent = section.closest('.page-content')
      if (!pageContent) return

      const header = section.querySelector('.e-gridheader')
      const pager = section.querySelector('.pagination-container')
      const headerBottom =
        header?.getBoundingClientRect().bottom ??
        section.getBoundingClientRect().top
      const pageBottom = pageContent.getBoundingClientRect().bottom
      const pagerHeight = pager?.getBoundingClientRect().height ?? 0
      const available = pageBottom - headerBottom - pagerHeight - 16

      setProviderGridBodyMaxHeight(Math.max(120, Math.floor(available)))
    }

    measureGridBodyMaxHeight()

    const pageContent = section.closest('.page-content')
    const resizeObserver = new ResizeObserver(measureGridBodyMaxHeight)
    resizeObserver.observe(section)
    if (pageContent) resizeObserver.observe(pageContent)

    window.addEventListener('resize', measureGridBodyMaxHeight)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measureGridBodyMaxHeight)
    }
  }, [loading, showEmptyState, transformedData.length])

  const renderWorkingDays = (workDay: WorkingSchedule[]): JSX.Element => {
    const displayLetters = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
    return (
      <div className="working-days-container">
        {Object.values(DayOfWeek).map((day, index) => (
          <WorkingDayBadge
            key={index}
            day={displayLetters[index]}
            isAvailable={workDay.some(
              (schedule) => schedule.day_of_week === day,
            )}
            title={day}
          />
        ))}
      </div>
    )
  }

  /** Makes the provider active (row, tray, side panel content) and opens the panel. */
  const handleSelectProvider = useCallback(
    (provider: Provider): void => {
      dispatch(setViewProvider(provider))
      dispatch(setActiveSidePanel('main'))
    },
    [dispatch],
  )

  const handleRowClick = useCallback(
    (args: RecordClickEventArgs): void => {
      const target = args.target as HTMLElement | undefined
      const provider = args.rowData as Provider | undefined
      if (!target || !provider) return
      if (target.closest('button, a, input, textarea, select')) return
      handleSelectProvider(provider)
    },
    [handleSelectProvider],
  )

  const columns: ColumnModel[] = useMemo(
    () => [
      {
        field: 'providerName',
        headerText: 'Staff name',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        template: (props: Provider) => {
          return (
            <div className="provider-info-column d-flex align-items-center">
              <i
                title={
                  props.employment_type === 'Full-Time'
                    ? 'Full-Time'
                    : props.employment_type === 'Part-Time'
                      ? 'Part-Time'
                      : ''
                }
                className={classNames('mgc me-1', {
                  mgc_time_line: props.employment_type === 'Full-Time',
                  mgc_time_duration_line: props.employment_type === 'Part-Time',
                })}
              ></i>
              <div>{getFullName(props)}</div>
            </div>
          )
        },
      },
      {
        field: 'work_role',
        headerText: 'Role',
        width: 100,
        template: (props: Provider) => {
          if (!props.work_role) return null
          return (
            <div className="provider-work-role-column">
              <ProviderRoleBadge
                workPosition={props.work_role as ProviderWorkPosition}
              />
            </div>
          )
        },
        clipMode: ClipMode.EllipsisWithTooltip,
      },
      {
        field: 'contactNumber',
        headerText: 'Contact number',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        width: 100,
        clipMode: ClipMode.EllipsisWithTooltip,
      },
      {
        field: 'email',
        headerText: 'Email',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        width: 150,
        clipMode: ClipMode.EllipsisWithTooltip,
      },
      {
        field: 'completeAddress',
        headerText: 'Address',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        width: 280,
        clipMode: ClipMode.EllipsisWithTooltip,
      },
      {
        field: '',
        headerText: 'Working days',
        headerTextAlign: TextAlign.Left,
        textAlign: TextAlign.Left,
        width: 150,
        template: (props: Provider) => (
          <div className="provider-working-days-column">
            {renderWorkingDays(props.workingHours || [])}
          </div>
        ),
      },
      {
        field: 'work_status',
        headerText: 'Work status',
        width: 80,
        template: (props: Provider) => (
          <StatusBadge status={props.work_status as StatusTypes} />
        ),
      },
      {
        field: '',
        headerText: 'Actions',
        headerTextAlign: TextAlign.Center,
        textAlign: TextAlign.Center,
        width: 100,
        template: (props: Provider) => {
          return (
            <ActionButtons
              showEdit={false}
              showDelete={false}
              onView={() => handleSelectProvider(props)}
              onPhone={() => handleCallClick(props)}
              onMail={() => handleEmailClick(props)}
              onMessage={() => handleMessageClick(props)}
            />
          )
        },
      },
    ],
    [
      handleSelectProvider,
      handleCallClick,
      handleEmailClick,
      handleMessageClick,
    ],
  )

  useEffect(() => {
    if (loading && !showingDevMocks) {
      showLoading({
        container: GLOBAL_LOADING_CONTAINER,
        hideElements: [
          '.provider-table-container .provider-header-row-top + div',
        ],
      })
    } else {
      hideLoading(GLOBAL_LOADING_CONTAINER)
    }
    return () => hideLoading(GLOBAL_LOADING_CONTAINER)
  }, [loading, showingDevMocks, showLoading, hideLoading])

  return (
    <div className="provider-management-container">
      <Container fluid className="provider-table-container p-0">
        <div className="d-flex flex-column">
          <div className="d-flex align-items-end justify-content-end gap-2 padd-812 w-100 provider-header-row-top">
            <div className="d-flex align-items-center gap-2">
              <SearchWithFilter
                searchIcon="mgc_search_2_line"
                filterIcon="mgc_settings_2_line"
                filterLabel=""
                placeholder="Search staff..."
                searchQuery={searchQuery}
                onChange={handleSearchChange}
                onFilterChange={handleFilterChange}
                onReset={clearAllFilters}
                filters={filterData}
                filterLabels={filterLabels}
                className="search-provider-filter"
              />
            </div>
            {/* Temporarily hidden per request:
            <Button
              size="md"
              className="button-primary"
              onClick={handleAddProvider}
            >
              Add Staff
            </Button>
            */}
          </div>
          <div
            ref={providerGridSectionRef}
            className="padd-812 provider-grid-content"
            style={
              providerGridBodyMaxHeight != null
                ? ({
                    '--provider-grid-scroll-max-height': `${providerGridBodyMaxHeight}px`,
                  } as React.CSSProperties)
                : undefined
            }
          >
            {showEmptyState ? (
              <EmptyState
                className="provider-management-empty"
                iconClass="mgc mgc_group_3_fill"
                title="No staff found"
                description={
                  hasNoStaff
                    ? "You don't have any staff on record yet."
                    : 'No staff match your search.'
                }
                actionLabel={hasNoStaff ? 'Add staff' : undefined}
                onAction={hasNoStaff ? handleAddProvider : undefined}
              />
            ) : (
              <DataGridComponent
                ref={providerGridRef}
                data={transformedData}
                columns={columns}
                allowPaging={true}
                sortSettings={{
                  columns: [{ field: 'providerName', direction: 'Ascending' }],
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                recordClick={handleRowClick}
                dataBound={syncGridSelection}
              />
            )}
          </div>
        </div>
      </Container>
      {/* Provider Actions Modals */}
      {modalState.showProviderModal && (
        <AddProviderModal
          isOpen={modalState.showProviderModal}
          onRequestClose={handleCloseProviderModal}
          providerData={
            modalState.selectedProvider
              ? mapDatabaseToProviderData(modalState.selectedProvider)
              : undefined
          }
        />
      )}
      <EmailSender
        show={modalState.showEmailSender}
        onHide={handleCloseEmailSender}
        recipientEmail={modalState.selectedProviderContact?.email ?? ''}
      />
      {modalState.showCallModal && modalState.selectedProviderContact && (
        <SelectProviderContactModal
          show={modalState.showCallModal}
          onHide={handleCloseCallModal}
          providerData={modalState.selectedProviderContact}
        />
      )}

      <ConfirmDeleteModal
        show={modalState.showConfirmDeleteModal}
        onHide={handleCloseConfirmDeleteModal}
        onConfirm={() =>
          modalState.providerToDelete &&
          confirmDeleteProvider(modalState.providerToDelete)
        }
        entityName={
          modalState.providerToDelete
            ? getFullName(modalState.providerToDelete)
            : ''
        }
      />
      {alertMessageInfo.show && (
        <AlertMessage
          show={alertMessageInfo.show}
          onHide={() =>
            setAlertMessageInfo({ ...alertMessageInfo, show: false })
          }
          title={alertMessageInfo.title}
          message={alertMessageInfo.message}
          type={alertMessageInfo.type}
        />
      )}
    </div>
  )
}

export default ProviderManagement
